package org.zest.insan_360_website.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.zest.insan_360_website.utils.rememberViewportWidth
import org.zest.insan_360_website.utils.LG
import org.zest.insan_360_website.utils.MD
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.Div
import org.zest.insan_360_website.components.widgets.LocalModal
import org.zest.insan_360_website.components.widgets.LocalToast
import org.zest.insan_360_website.components.widgets.ModalState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.UserRole

enum class DashboardPages(val allowedRoles: List<UserRole>, val pageName: String) {
    MAIN(listOf(UserRole.STUDENT, UserRole.ADMIN, UserRole.SUPER_ADMIN), "main"),
    CREDITS(listOf(UserRole.ADMIN, UserRole.SUPER_ADMIN), "credits"),
    STUDENTS(listOf(UserRole.ADMIN), "students"),
    ADMINS(listOf(UserRole.SUPER_ADMIN), "admins"),
    REPORT(listOf(UserRole.STUDENT, UserRole.ADMIN, UserRole.SUPER_ADMIN), "report"),
    STUDENT_AI(listOf(UserRole.STUDENT), "life-coach"),
    ADMIN_AI(listOf(UserRole.ADMIN), "coach-assistant"),
    SUPPORT(listOf(UserRole.STUDENT, UserRole.ADMIN), "support"),
    SETTINGS(listOf(UserRole.STUDENT, UserRole.ADMIN, UserRole.SUPER_ADMIN), "settings");

    fun isAllowedFor(role: UserRole): Boolean {
        return allowedRoles.contains(role)
    }
}

val userPageColor = Colors.White
val userPageBGColor = Color.rgb(245, 245, 245)
val userPageCardColor = Colors.White
//val userPageBorderColor = Color.rgb(226, 232, 240)
val userPageBorderStrongColor = Color.rgb(203, 213, 225)
val userPageButtonColor = Color.rgb(20, 184, 166)
val userPageRedButtonColor = Color.rgb(220, 20, 60)
val userPagePrimaryTextColor = Color.rgb(15, 23, 42)
val userPageSecondaryTextColor = Color.rgb(71, 85, 105)

val BaseDashboardGridStyle_1 = CssStyle {
    base {
        Modifier
            .grid {
                columns { repeat(1) { size(1.fr) } }
                rows { repeat(5) { size(1.fr) } }
            }
    }
    Breakpoint.MD {
        Modifier
            .grid {
                columns { repeat(2) { size(1.fr) } }
                rows { repeat(3) { size(1.fr) } }
            }
    }
    Breakpoint.XL {
        Modifier
            .grid {
                columns { repeat(3) { size(1.fr) } }
                rows { repeat(3) { size(1.fr) } }
            }
    }
}

val dashboardLayout = CssStyle {
    base {
        Modifier
            .grid {
                columns { size(1.fr); size(80.px) }
                rows { size(80.px); size(1.fr) }
            }
    }
    Breakpoint.LG { // 992.px
        Modifier
            .gap(0.25.cssRem)
            .grid {
                rows { size(6.cssRem); size(1.fr) }
                columns { size(12.cssRem); size(1.fr) }
            }
    }
}

val contentBoxStyle = CssStyle {
    base {
        Modifier
            .borderRadius(topLeft = 1.cssRem, topRight = 1.cssRem)
            .padding(0.5.em)
    }
    Breakpoint.MD {
        Modifier
            .borderRadius(topLeft = 1.cssRem)
            .padding(2.em)
    }
}

val dashboardNavElementStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
            .borderRadius(0.75.cssRem)
            .padding(0.5.cssRem)
            .gap(1.cssRem)
            .fontSize(1.cssRem)
            .fontWeight(FontWeight.Bold)
            .cursor(Cursor.Pointer)
            .transition {
                property("background-color")
                duration(0.25.s)
            }
    }
}
val disabledContentButtonStyle = CssStyle {
    base {
        BaseButtonModifier
            .backgroundColor(userPageButtonColor.darkened(0.25f))
            .cursor(Cursor.NotAllowed)
    }

    cssRule("> .content-button-text") {
        Modifier.color(userPageBGColor.darkened(0.15f))
    }
}
val BaseButtonModifier = Modifier
    .padding(leftRight = 0.75.cssRem, topBottom = 0.5.cssRem)
    .borderRadius(0.5.cssRem)
    .cursor(Cursor.Pointer)
    .transition {
        property("background-color")
        duration(0.25.s)
    }
    .userSelect(UserSelect.None)
val contentButtonStyle = CssStyle {
    base {
        BaseButtonModifier.backgroundColor(userPageButtonColor)
    }
    hover { Modifier.backgroundColor(userPageButtonColor.darkened(0.1f)) }
    cssRule(":hover .content-button-text") {
        Modifier.color(userPageBGColor.darkened(0.1f))
    }
    cssRule(":hover ~ .content-background-icon") {
        Modifier
            .opacity(1)
            .transform { translate(tx = 100.percent, ty = 0.percent) }
    }
}
val neutralContentButtonStyle = CssStyle {
    base {
        BaseButtonModifier.backgroundColor(userPageBGColor)
    }
    hover { Modifier.backgroundColor(userPageBGColor.darkened(0.1f)) }
    active { Modifier.backgroundColor(userPageBGColor.darkened(0.2f)) }
}
val contentRedButtonStyle = CssStyle {
    base {
        BaseButtonModifier.backgroundColor(userPageRedButtonColor)
    }
    hover { Modifier.backgroundColor(userPageRedButtonColor.darkened(0.1f)) }
    cssRule(":hover .content-button-text") {
        Modifier.color(userPageBGColor.darkened(0.1f))
    }
}
val contentButtonTextStyle = CssStyle {
    base {
        Modifier
            .color(userPageCardColor)
            .fontSize(0.75.em)
            .fontWeight(FontWeight.Bold)
    }
}

val profileListContentLayout = CssStyle {
    base {
        Modifier.grid {
            columns {
                repeat(1) { size(1.fr) }
            }

        }
    }
    Breakpoint.MD {
        Modifier.grid {
            columns {
                repeat(2) { size(1.fr) }
            }
        }
    }
    Breakpoint.XL {
        Modifier.grid {
            columns {
                repeat(3) { size(1.fr) }
            }
        }
    }
}

val userDashWordLibrary = LanguageSettings.UserDashboardPage

@OptIn(DelicateApi::class)
@Composable
@Layout
fun UserDashboardLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val currentPage = getCurrentPage(ctx)
    val modal = LocalModal.current
    val toast = LocalToast.current
    val viewportWidth = rememberViewportWidth()

    LaunchedEffect(Unit) { modal.hide() }

    LaunchedEffect(ctx.route.path, AuthState.token) {
        AuthState.load()
        if (!AuthState.isLoggedIn) {
            ctx.router.navigateTo("/?needToLogin=true")
            return@LaunchedEffect
        }
        if (!currentPage.isAllowedFor(AuthState.role)) {
            toast.show(
                "${LanguageSettings.General.access_denied_role[UserPreferences.language] ?: "Access Denied for role"}: " +
                        "${AuthState.role}",
                ToastType.ERROR
            )
            ctx.router.navigateTo("/user")
            return@LaunchedEffect
        }
    }

    Box(
        modifier = Modifier
            .backgroundColor(userPageBGColor)
            .minHeight(100.vh)
            .width(100.vw)
            .userSelect(UserSelect.None)
            .then(dashboardLayout.toModifier())

    ) {
        if (viewportWidth < LG) {
            Box(
                Modifier
                    .fillMaxSize()
                    .gridItem(1,2),
                contentAlignment = Alignment.Center
            ) {
                FaBars(
                    Modifier
                        .onClick {
                            modal.show(Modifier.padding(0.5.cssRem)) {
                                ModalPagesNav(currentPage = currentPage, modal = modal, ctx = ctx)
                            }
                        }
                        .cursor(Cursor.Pointer)
                        .zIndex(10),
                    IconSize.XL
                )
            }
            UserInfo(Modifier.gridItem(1, 1))
            ContentBox(Modifier.gridItem(2, 1, 2,1)) { content() }
        }
        if (viewportWidth >= LG) {
            TopLeft()
            UserInfo(Modifier.gridItem(1, 2))
            ContentBox(Modifier.gridItem(2, 2)) { content() }
            SidePagesNav(Modifier.gridItem(2, 1), currentPage, ctx)
        }
    }
}

@Composable
fun ContentBox(modifier: Modifier = Modifier, content: @Composable (() -> Unit)) {
    Box(
        modifier
            .backgroundColor(userPageColor)
            .fillMaxSize()
            .border {
                width(1.px)
                color(userPageBorderStrongColor)
                style(LineStyle.Solid)
            }
            .boxShadow(
                offsetX = (-2).px,
                offsetY = 6.px,
                blurRadius = 6.px,
                spreadRadius = 4.px,
                color = userPageBGColor.darkened(0.15f)
            )
            .then(contentBoxStyle.toModifier())
    ) {
        content()
    }
}

@Composable
fun SidePagesNav(
    modifier: Modifier = Modifier,
    currentPage: DashboardPages,
    ctx: PageContext
) {
    Column(
        modifier
            .fillMaxWidth()
            .minHeight(100.percent)
            .padding(topBottom = 16.px, leftRight = 8.px)
            .justifyContent(JustifyContent.SpaceBetween)
            .userSelect(UserSelect.None),
    ) {
        Column(
            Modifier
                .fillMaxSize()
                .minHeight(100.percent)
                .gap(0.5.cssRem)
        ) {
            DashboardPages.entries
                .filter { it.isAllowedFor(AuthState.role) }
                .forEach { page ->
                    NavElement(page, currentPage == page, true) { clickedPage ->
                        val path =
                            if (clickedPage == DashboardPages.MAIN) "/user" else "/user/${clickedPage.pageName.lowercase()}"
                        ctx.router.navigateTo(path)
                    }
                }
        }
    }
}

@Composable
fun ModalPagesNav(
    modifier: Modifier = Modifier,
    currentPage: DashboardPages,
    modal: ModalState,
    ctx: PageContext
) {
    Column(
        modifier
            .fillMaxSize()
            .justifyContent(JustifyContent.SpaceBetween)
            .userSelect(UserSelect.None)
    ) {
        Column(
            Modifier
                .fillMaxSize()
                .minHeight(100.percent)
                .gap(0.5.cssRem)
        ) {
            DashboardPages.entries
                .filter { it.isAllowedFor(AuthState.role) }
                .forEach { page ->
                    NavElement(page, currentPage == page, false) { clickedPage ->
                        val path =
                            if (clickedPage == DashboardPages.MAIN) "/user" else "/user/${clickedPage.pageName.lowercase()}"
                        window.history.replaceState(null, "", window.location.href)
                        modal.hide(fromBackButton = true)
                        ctx.router.navigateTo(path, UpdateHistoryMode.REPLACE)
                    }
                }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun UserInfo(modifier: Modifier = Modifier) {
    Box(
        modifier
            .fillMaxSize()
            .position(Position.Relative)
            .padding(leftRight = 16.px, topBottom = 8.px)
            .overflow(Overflow.Clip),
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .fontWeight(FontWeight.Bold)
                .zIndex(1)
        ) {
            Row(
                Modifier.gap(0.5.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                val displayName = AuthState.name ?: "User"
                val viewportWidth = rememberViewportWidth()
                val maxLen = if (viewportWidth < MD) 50 else 70
                val truncatedName = if (displayName.length > maxLen) "${displayName.take(maxLen)}..." else displayName
                SpanText(
                    truncatedName,
                    Modifier
                        .color(userPagePrimaryTextColor)
                        .fontSize(1.em)
                        .textOverflow(TextOverflow.Ellipsis),
                )
                Box(
                    Modifier
                        .padding(0.25.cssRem)
                        .border {
                            style(LineStyle.Solid)
                            width(1.px)
                            color(userPagePrimaryTextColor)
                        }
                        .borderRadius(0.5.cssRem)
                        .flexShrink(0),
                    contentAlignment = Alignment.Center
                ) {
                    val roleText = when (AuthState.role) {
                        UserRole.STUDENT -> "CLIENT"
                        UserRole.ADMIN -> "ADMIN"
                        UserRole.SUPER_ADMIN -> "SUPER ADMIN"
                    }
                    SpanText(
                        roleText,
                        Modifier
                            .color(userPagePrimaryTextColor)
                            .fontSize(0.6.em)
                            .fontWeight(FontWeight.Bold)
                            .userSelect(UserSelect.None)
                    )
                }
            }
            SpanText(
                AuthState.email ?: "user@email.com",
                Modifier
                    .color(userPageSecondaryTextColor)
                    .fontSize(0.75.em),
            )
        }
    }
}

@Composable
fun TopLeft(modifier: Modifier = Modifier) {
    Box(
        modifier.fillMaxSize(),
        Alignment.Center
    ) {
        Image(
            "/images/insan360_logo_notype.webp",
            "Insan 360",
            Modifier
                .width(auto)
                .height(85.percent)
                .rotate(5.deg)
        )
    }
}

@Composable
fun NavElement(page: DashboardPages, isOpen: Boolean, isSide: Boolean, onClick: (DashboardPages) -> Unit) {
    var isActive by mutableStateOf(false)
    Box(
        Modifier
            .fillMaxWidth()
            .height(auto)
            .translateX(if (isOpen && isSide) 1.5.cssRem else 0.px)
            .transition {
                property("translate")
                duration(0.25.s)
            }
            .onClick { onClick(page) }
    ) {
        Row(
            Modifier
                .onMouseEnter { isActive = true }
                .onMouseLeave { isActive = false }
                .onTouchStart { isActive = true }
                .onTouchEnd { isActive = false }
                .backgroundColor(
                    if (isOpen) userPageColor
                    else if (isActive) userPageColor
                    else userPageBGColor
                )
                .border {
                    width(if (isOpen) 1.px else 0.px)
                    color(userPageBorderStrongColor)
                    style(LineStyle.Solid)
                }
                .then(dashboardNavElementStyle.toModifier())
                .thenIf(!isSide) {
                    Modifier.borderRadius(1.cssRem)
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            val colorModifier =
                Modifier.color(if (isOpen) userPagePrimaryTextColor else userPagePrimaryTextColor.lightened(0.25f))
            Box(
                Modifier
                    .size(2.5.cssRem),
                contentAlignment = Alignment.Center
            ) {
                when (page) {
                    DashboardPages.MAIN -> FaHouse(colorModifier)
                    DashboardPages.REPORT -> FaFile(colorModifier, IconStyle.FILLED)
                    DashboardPages.SETTINGS -> FaGear(colorModifier)
                    DashboardPages.SUPPORT -> FaPhone(colorModifier)
                    DashboardPages.CREDITS -> FaTicketSimple(colorModifier)
                    DashboardPages.STUDENTS -> FaUser(colorModifier, IconStyle.FILLED)
                    DashboardPages.ADMINS -> FaUserTie(colorModifier)
                    DashboardPages.STUDENT_AI -> FaComment(colorModifier, IconStyle.FILLED)
                    DashboardPages.ADMIN_AI -> FaComment(colorModifier, IconStyle.FILLED)
                }
            }
            val text = when (page) {
                DashboardPages.MAIN -> userDashWordLibrary.home[UserPreferences.language]
                    ?: "Home"

                DashboardPages.REPORT -> userDashWordLibrary.report[UserPreferences.language]
                    ?: "Assessment Report"

                DashboardPages.SUPPORT -> userDashWordLibrary.support[UserPreferences.language]
                    ?: "Settings"

                DashboardPages.SETTINGS -> userDashWordLibrary.settings[UserPreferences.language]
                    ?: "Customer Support"

                DashboardPages.CREDITS -> userDashWordLibrary.credits[UserPreferences.language]
                    ?: "Credit Handling"

                DashboardPages.STUDENTS -> userDashWordLibrary.students[UserPreferences.language]
                    ?: "Students Handling"

                DashboardPages.ADMINS -> userDashWordLibrary.admins[UserPreferences.language]
                    ?: "Admins Handling"

                DashboardPages.STUDENT_AI -> userDashWordLibrary.studentAi[UserPreferences.language]
                    ?: "Life Coach"

                DashboardPages.ADMIN_AI -> userDashWordLibrary.adminAi[UserPreferences.language]
                    ?: "Coach Assistant"
            }
            SpanText(
                text,
                colorModifier.then(
                    Modifier.fontSize(0.8.cssRem)
                )
            )
        }
    }
}

@Composable
fun PageContentGridLayout(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Div(
        modifier
            .fillMaxSize()
            .gap(0.5.cssRem)
            .toAttrs()
    ) {
        content()
    }
}

@Composable
fun PageContentContainer(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Box(
        Modifier
            .fillMaxSize()
            .borderRadius(1.cssRem)
            .border {
                width(1.px)
                color(userPageBorderStrongColor)
                style(LineStyle.Solid)
            }
            .backgroundColor(userPageCardColor)
            .padding(0.5.cssRem)
            .display(DisplayStyle.Flex)
            .then(modifier)
    ) {
        content()
    }
}

fun getCurrentPage(ctx: PageContext): DashboardPages {
    val path = ctx.route.path

    return when {
        path == "/user" -> DashboardPages.MAIN
        path.startsWith("/user/report") -> DashboardPages.REPORT
        path.startsWith("/user/support") -> DashboardPages.SUPPORT
        path.startsWith("/user/settings") -> DashboardPages.SETTINGS
        path.startsWith("/user/credits") -> DashboardPages.CREDITS
        path.startsWith("/user/students") -> DashboardPages.STUDENTS
        path.startsWith("/user/admins") -> DashboardPages.ADMINS
        path.startsWith("/user/life-coach") -> DashboardPages.STUDENT_AI
        path.startsWith("/user/coach-assistant") -> DashboardPages.ADMIN_AI
        else -> DashboardPages.MAIN
    }
}

val containerShadowStyle = CssStyle {
    base {
        Modifier
            .boxShadow(
                offsetY = 4.px,
                blurRadius = 4.px,
                spreadRadius = (-1).px,
                color = userPageColor.darkened(0.2f)
            )
    }
}

@Composable
fun PageContentBaseCol(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Column(
        Modifier
            .fillMaxSize()
            .gap(0.5.cssRem)
            .then(modifier)
    ) {
        content()
    }
}